<template>
  <HrPageLayout
    title="Apply for Leave"
    subtitle="My Leave"
    :breadcrumb-items="[
      { label: 'Dashboard', route: '/employee-dashboard' },
      { label: 'My Leave', route: '/employee-leave-history' },
      { label: 'Apply for Leave' }
    ]"
  >
          <!-- Leave Balance Summary -->
          <div class="row">
            <div class="col-12">
              <div class="card bg-light">
                <div class="card-body">
                  <h5 class="mb-3">
                    <i class="mdi mdi-information-outline text-info me-2"></i>
                    Your Leave Balance
                  </h5>

                  <div v-if="loadingBalance" class="text-center py-3">
                    <div class="spinner-border spinner-border-sm text-primary"></div>
                    <span class="ms-2">Loading balances...</span>
                  </div>

                  <div v-else-if="leaveBalances.length > 0" class="row">
                    <div v-for="balance in leaveBalances.slice(0, 6)" :key="balance.leaveTypeID" class="col-md-2 col-sm-4 col-6 mb-2">
                      <div class="text-center p-2 bg-white rounded shadow-sm">
                        <h4 class="mb-0 text-primary">{{ balance.remainingDays || 0 }}</h4>
                        <small class="text-muted">{{ balance.leaveTypeName }}</small>
                      </div>
                    </div>
                  </div>

                  <div v-else class="text-center text-muted py-3">
                    No leave balance information available
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Leave Application Form -->
          <div class="row">
            <div class="col-lg-8">
              <div class="card">
                <div class="card-header bg-primary text-white">
                  <h5 class="card-title text-white mb-0">
                    <i class="mdi mdi-file-document-edit me-2"></i>
                    Leave Application Form
                  </h5>
                </div>
                <div class="card-body">
                  <form @submit.prevent="submitLeaveRequest">
                    <!-- Leave Type & Duration Type -->
                    <div class="row mb-3">
                      <div class="col-md-6">
                        <label class="form-label">Leave Type <span class="text-danger">*</span></label>
                        <select v-model="formData.leaveTypeID" @change="onLeaveTypeChange" class="form-select" required>
                          <option value="">Select Leave Type</option>
                          <option v-for="type in leaveTypes" :key="type.id" :value="type.id">
                            {{ type.leaveTypeName }} ({{ getAvailableDaysForType(type.id) }} days available)
                          </option>
                        </select>
                        <small v-if="selectedLeaveType" class="text-muted">
                          {{ selectedLeaveType.description }}
                        </small>
                      </div>

                      <div class="col-md-6">
                        <label class="form-label">Duration Type <span class="text-danger">*</span></label>
                        <div class="d-flex gap-4 mt-2">
                          <div class="form-check">
                            <input
                                class="form-check-input"
                                type="radio"
                                id="fullDay"
                                value="full"
                                v-model="durationType"
                            >
                            <label class="form-check-label" for="fullDay">
                              <i class="mdi mdi-calendar-blank me-1"></i>
                              Full Day(s)
                            </label>
                          </div>
                          <div class="form-check">
                            <input
                                class="form-check-input"
                                type="radio"
                                id="halfDay"
                                value="half"
                                v-model="durationType"
                            >
                            <label class="form-check-label" for="halfDay">
                              <i class="mdi mdi-calendar-clock me-1"></i>
                              Half Day
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Date Range -->
                    <div class="row mb-3">
                      <div class="col-md-6">
                        <label class="form-label">Start Date <span class="text-danger">*</span></label>
                        <input v-model="formData.startDate"
                               @change="calculateDays"
                               type="date"
                               :min="minDate"
                               class="form-control"
                               required>
                      </div>
                      <div class="col-md-6">
                        <label class="form-label">
                          {{ durationType === 'half' ? 'Date' : 'End Date' }}
                          <span class="text-danger">*</span>
                        </label>
                        <input v-model="formData.endDate"
                               @change="calculateDays"
                               type="date"
                               :min="formData.startDate || minDate"
                               :disabled="durationType === 'half'"
                               class="form-control"
                               required>
                      </div>
                    </div>

                    <!-- Half Day Session Options -->
                    <div v-if="durationType === 'half'" class="row mb-3">
                      <div class="col-md-12">
                        <label class="form-label">Half Day Session <span class="text-danger">*</span></label>
                        <div class="d-flex gap-4">
                          <div class="form-check form-check-inline">
                            <input
                                class="form-check-input"
                                type="radio"
                                id="morning"
                                value="morning"
                                v-model="halfDaySession"
                            >
                            <label class="form-check-label" for="morning">
                              <i class="mdi mdi-weather-sunny text-warning me-1"></i>
                              Morning (First Half)
                              <small class="text-muted d-block">8:00 AM - 12:00 PM</small>
                            </label>
                          </div>
                          <div class="form-check form-check-inline">
                            <input
                                class="form-check-input"
                                type="radio"
                                id="afternoon"
                                value="afternoon"
                                v-model="halfDaySession"
                            >
                            <label class="form-check-label" for="afternoon">
                              <i class="mdi mdi-weather-night text-info me-1"></i>
                              Afternoon (Second Half)
                              <small class="text-muted d-block">1:00 PM - 5:00 PM</small>
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Calculated Days Display -->
                    <div class="row mb-3">
                      <div class="col-md-12">
                        <div class="alert alert-info py-2 mb-0">
                          <div class="d-flex align-items-center justify-content-between">
                            <div>
                              <i class="mdi mdi-information me-2"></i>
                              <strong>Total Days Requested:</strong>
                              <span class="badge bg-primary ms-2">{{ calculatedDays }} day(s)</span>
                              <span v-if="durationType === 'half'" class="badge bg-secondary ms-1">
                                {{ halfDaySession === 'morning' ? 'Morning' : 'Afternoon' }}
                              </span>
                            </div>
                            <div v-if="leaveBalance">
                              <strong>Remaining Balance:</strong>
                              <span class="badge" :class="leaveBalance.remainingDays >= calculatedDays ? 'bg-success' : 'bg-danger'">
                                {{ leaveBalance.remainingDays }} days
                              </span>
                            </div>
                          </div>
                          <div v-if="calculatedDays > (leaveBalance?.remainingDays || 0)" class="text-danger mt-2">
                            <i class="mdi mdi-alert-circle"></i>
                            Insufficient balance! You need {{ calculatedDays }} days but only have {{ leaveBalance?.remainingDays || 0 }} days available.
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Reason -->
                    <div class="mb-3">
                      <label class="form-label">Reason for Leave <span class="text-danger">*</span></label>
                      <textarea v-model="formData.reason"
                                class="form-control"
                                rows="4"
                                placeholder="Please provide a detailed reason for your leave request"
                                maxlength="500"
                                required></textarea>
                      <small class="text-muted">{{ formData.reason.length }}/500 characters</small>
                    </div>

                    <!-- Contact Information -->
                    <div class="row">
                      <div class="col-md-6 mb-3">
                        <label class="form-label">Contact Number During Leave</label>
                        <input v-model="formData.contactNumber"
                               type="tel"
                               class="form-control"
                               placeholder="Phone number during leave">
                      </div>
                      <div class="col-md-6 mb-3">
                        <label class="form-label">Alternate Contact Person</label>
                        <input v-model="formData.alternateContact"
                               type="text"
                               class="form-control"
                               placeholder="Name and contact number">
                      </div>
                    </div>

                    <!-- Supporting Document -->
                    <div class="mb-4">
                      <label class="form-label">Supporting Document (Optional)</label>
                      <input @change="handleFileUpload"
                             type="file"
                             class="form-control"
                             accept=".pdf,.jpg,.jpeg,.png,.doc,.docx">
                      <small class="text-muted">Upload medical certificate, invitation letter, etc. (Max 5MB)</small>
                    </div>

                    <!-- Submit Buttons -->
                    <div class="d-flex justify-content-end gap-2 border-top pt-3">
                      <button @click="gotTo('employee-leave-history')"
                              type="button"
                              class="btn btn-outline-secondary">
                        <i class="mdi mdi-arrow-left me-1"></i>
                        Cancel
                      </button>
                      <button type="submit"
                              class="btn btn-primary"
                              :disabled="submitting || !isFormValid">
                        <span v-if="submitting">
                          <span class="spinner-border spinner-border-sm me-1"></span>
                          Submitting...
                        </span>
                        <span v-else>
                          <i class="mdi mdi-send me-1"></i>
                          Submit Leave Request
                        </span>
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>

            <!-- Instructions Sidebar -->
            <div class="col-lg-4">
              <div class="card">
                <div class="card-header bg-info text-white">
                  <h6 class="card-title text-white mb-0">
                    <i class="mdi mdi-help-circle me-2"></i>
                    Important Instructions
                  </h6>
                </div>
                <div class="card-body">
                  <ul class="list-unstyled mb-0">
                    <li class="mb-3">
                      <i class="mdi mdi-check-circle text-success me-2"></i>
                      <strong>Submit in Advance:</strong>
                      <p class="text-muted mb-0 small">Apply for leave at least 3 days before the start date.</p>
                    </li>
                    <li class="mb-3">
                      <i class="mdi mdi-check-circle text-success me-2"></i>
                      <strong>Half Day Option:</strong>
                      <p class="text-muted mb-0 small">Select half day for morning (8AM-12PM) or afternoon (1PM-5PM) leave.</p>
                    </li>
                    <li class="mb-3">
                      <i class="mdi mdi-check-circle text-success me-2"></i>
                      <strong>Check Balance:</strong>
                      <p class="text-muted mb-0 small">Ensure you have sufficient leave balance before applying.</p>
                    </li>
                    <li class="mb-3">
                      <i class="mdi mdi-check-circle text-success me-2"></i>
                      <strong>Supporting Documents:</strong>
                      <p class="text-muted mb-0 small">Medical certificates required for sick leave > 3 days.</p>
                    </li>
                    <li class="mb-3">
                      <i class="mdi mdi-check-circle text-success me-2"></i>
                      <strong>Supervisor Approval:</strong>
                      <p class="text-muted mb-0 small">Your request will be sent to your manager for approval.</p>
                    </li>
                  </ul>
                </div>
              </div>

              <!-- Leave Policies -->
              <div class="card">
                <div class="card-header">
                  <h6 class="card-title mb-0">
                    <i class="mdi mdi-file-document me-2"></i>
                    Leave Policies
                  </h6>
                </div>
                <div class="card-body">
                  <div class="mb-3">
                    <h6 class="text-primary">Annual Leave</h6>
                    <p class="text-muted small mb-0">21 days per year, can be carried forward (max 10 days)</p>
                  </div>
                  <div class="mb-3">
                    <h6 class="text-primary">Sick Leave</h6>
                    <p class="text-muted small mb-0">14 days per year, medical certificate required > 3 days</p>
                  </div>
                  <div class="mb-3">
                    <h6 class="text-primary">Maternity/Paternity</h6>
                    <p class="text-muted small mb-0">90 days for maternity, 14 days for paternity</p>
                  </div>
                  <div>
                    <a href="javascript:void(0)" class="text-primary small">
                      <i class="mdi mdi-book-open-variant me-1"></i>
                      View Full Leave Policy
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

  </HrPageLayout>
</template>

<script>
/* eslint-disable */
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { authUrl, execute } from "@/api";

export default {
  name: "EmployeeLeaveApply",
  components: { HrPageLayout },

  data() {
    return {
      loadingBalance: false,
      submitting: false,
      leaveTypes: [],
      leaveBalances: [],
      formData: {
        leaveTypeID: '',
        leaveTypeName: '',
        startDate: '',
        endDate: '',
        reason: '',
        contactNumber: '',
        alternateContact: '',
        document: null
      },
      durationType: 'full',
      halfDaySession: 'morning',
      calculatedDays: 0,
      minDate: new Date().toISOString().split('T')[0]
    };
  },

  computed: {
    selectedLeaveType() {
      return this.leaveTypes.find(t => t.id == this.formData.leaveTypeID);
    },

    leaveBalance() {
      return this.leaveBalances.find(b => b.leaveTypeID == this.formData.leaveTypeID);
    },

    isFormValid() {
      return (
          this.formData.leaveTypeID &&
          this.formData.startDate &&
          this.formData.endDate &&
          this.formData.reason.trim().length > 0 &&
          this.calculatedDays > 0 &&
          this.calculatedDays <= (this.leaveBalance?.remainingDays || 0)
      );
    }
  },

  watch: {
    durationType(newVal) {
      if (newVal === 'half') {
        // For half day, end date = start date and days = 0.5
        this.formData.endDate = this.formData.startDate;
        this.calculatedDays = 0.5;
      } else {
        this.calculateDays();
      }
    },
    'formData.startDate'(newVal) {
      if (this.durationType === 'half') {
        this.formData.endDate = newVal;
        this.calculatedDays = 0.5;
      }
    }
  },

  mounted() {
    this.loadLeaveTypes();
    this.loadLeaveBalance();
  },

  methods: {
    async loadLeaveTypes() {
      try {
        const data = new FormData();
        data.append("function", "getLeaveTypes");

        const res = await execute(data, authUrl);

        if (res && res.data && res.data.success) {
          this.leaveTypes = res.data.data;
        }
      } catch (error) {
        console.error("Failed to load leave types:", error);
      }
    },

    async loadLeaveBalance() {
      this.loadingBalance = true;

      try {
        const data = new FormData();
        data.append("function", "getEmployeeLeaveBalance");

        const res = await execute(data, authUrl);

        if (res && res.data && res.data.success) {
          this.leaveBalances = res.data.data;
        }
      } catch (error) {
        console.error("Failed to load leave balance:", error);
      } finally {
        this.loadingBalance = false;
      }
    },

    onLeaveTypeChange() {
      if (this.selectedLeaveType) {
        this.formData.leaveTypeName = this.selectedLeaveType.leaveTypeName;
      }
      this.calculateDays();
    },

    getAvailableDaysForType(leaveTypeId) {
      const balance = this.leaveBalances.find(b => b.leaveTypeID == leaveTypeId);
      return balance ? parseFloat(balance.remainingDays) : 0;
    },

    calculateDays() {
      if (!this.formData.startDate || !this.formData.endDate) {
        this.calculatedDays = 0;
        return;
      }

      // For half day, always 0.5
      if (this.durationType === 'half') {
        this.formData.endDate = this.formData.startDate;
        this.calculatedDays = 0.5;
        return;
      }

      const start = new Date(this.formData.startDate);
      const end = new Date(this.formData.endDate);

      if (end < start) {
        this.calculatedDays = 0;
        return;
      }

      let days = 0;
      let current = new Date(start);

      while (current <= end) {
        const dayOfWeek = current.getDay();
        // Count only weekdays (Monday to Friday)
        if (dayOfWeek !== 0 && dayOfWeek !== 6) {
          days++;
        }
        current.setDate(current.getDate() + 1);
      }

      this.calculatedDays = days;
    },

    handleFileUpload(event) {
      const file = event.target.files[0];
      if (file) {
        // Check file size (5MB max)
        if (file.size > 5 * 1024 * 1024) {
          this.$toast?.error("File size must be less than 5MB") ||
          alert("File size must be less than 5MB");
          event.target.value = '';
          return;
        }
        this.formData.document = file;
      }
    },

    async submitLeaveRequest() {
      // Validate
      if (!this.formData.leaveTypeID || !this.formData.startDate || !this.formData.endDate || !this.formData.reason) {
        this.$toast?.error("Please fill all required fields") ||
        alert("Please fill all required fields");
        return;
      }

      if (this.calculatedDays <= 0) {
        this.$toast?.error("Invalid date range") ||
        alert("Invalid date range");
        return;
      }

      if (this.leaveBalance && this.calculatedDays > this.leaveBalance.remainingDays) {
        this.$toast?.error(`Insufficient leave balance. You have ${this.leaveBalance.remainingDays} days remaining`) ||
        alert(`Insufficient leave balance. You have ${this.leaveBalance.remainingDays} days remaining`);
        return;
      }

      this.submitting = true;

      try {
        const data = new FormData();
        data.append("function", "applyForLeave");
        data.append("leaveTypeID", this.formData.leaveTypeID);
        data.append("leaveTypeName", this.formData.leaveTypeName);
        data.append("startDate", this.formData.startDate);
        data.append("endDate", this.formData.endDate);
        data.append("totalDays", this.calculatedDays);
        data.append("durationType", this.durationType);
        data.append("reason", this.formData.reason);
        data.append("contactNumber", this.formData.contactNumber);
        data.append("alternateContact", this.formData.alternateContact);

        // Add half day session if applicable
        if (this.durationType === 'half') {
          data.append("halfDaySession", this.halfDaySession);
        }

        if (this.formData.document) {
          data.append("document", this.formData.document);
        }

        const res = await execute(data, authUrl);

        if (res && res.data && res.data.success) {
          this.$toast?.success("Leave request submitted successfully!") ||
          alert("Leave request submitted successfully!");

          // Redirect to leave history
          setTimeout(() => {
            this.gotTo('employee-leave-history');
          }, 1500);
        } else {
          throw new Error(res.data?.message || 'Failed to submit leave request');
        }
      } catch (error) {
        this.$toast?.error("Error: " + error.message) ||
        alert("Error: " + error.message);
      } finally {
        this.submitting = false;
      }
    },

    gotTo(route) {
      sessionStorage.setItem("selectedSub", route);
      sessionStorage.setItem("currentPage", route);
      if (typeof route === "string" && route.startsWith("/")) {
        this.$router.push(route);
      } else {
        this.$router.push({ name: route });
      }
    }
  }
};
</script>

<style scoped>
.card {
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  margin-bottom: 1.5rem;
}

.card-header {
  border-bottom: 2px solid rgba(0, 0, 0, 0.1);
}

.form-label {
  font-weight: 500;
  margin-bottom: 0.5rem;
}

.form-control:focus,
.form-select:focus {
  border-color: #059669;
  box-shadow: 0 0 0 0.2rem rgba(91, 115, 232, 0.25);
}

.form-check-input:checked {
  background-color: #059669;
  border-color: #059669;
}

.form-check-label {
  cursor: pointer;
}

.form-check-inline {
  padding: 10px 20px;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  transition: all 0.2s;
}

.form-check-inline:hover {
  background-color: #f8f9fa;
}

.form-check-inline:has(input:checked) {
  background-color: rgba(91, 115, 232, 0.1);
  border-color: #059669;
}

.alert {
  border-left: 3px solid;
}

.alert-info {
  border-left-color: #17a2b8;
}

.list-unstyled li {
  border-bottom: 1px solid #f0f0f0;
  padding-bottom: 1rem;
}

.list-unstyled li:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.bg-light .bg-white {
  transition: transform 0.2s;
}

.bg-light .bg-white:hover {
  transform: translateY(-2px);
}

@media (max-width: 768px) {
  .card-body {
    padding: 1rem;
  }

  .form-check-inline {
    width: 100%;
    margin-bottom: 10px;
  }
}
</style>